<template>
  <Dialog
    :visible="visible"
    :title="$t(`${namespace}.setWebCallPermissionTitle`)"
    :isLoading="isLoading"
    max-width="638"
    @close="close()"
  >
    <div v-if="visible">
      <form novalidate @submit.prevent="batchUpdate">
        <v-card-text class="dialog__body">
          <p class="pb-2">{{ $t(`${namespace}.setWebCallPermissionMsg`) }}</p>
          <p><strong>{{ $t(`${namespace}.setWebCallPermissionStep1`) }}</strong></p>

          <v-radio-group
            v-model="radioGroup"
            v-validate="{ required: true }"
            :error-messages="errors.collect($t('Common.employees'))"
            :data-vv-name="$t('Common.employees')"
            required
          >
            <v-radio :label="$t(`${namespace}.allEmployees`)" value="allEmployees"></v-radio>
            <v-radio :label="$t(`${namespace}.selectEmployeesByName`)" value="perName"></v-radio>
            <v-radio :label="$t(`${namespace}.selectEmployeesById`)" value="perId"></v-radio>
          </v-radio-group>

          <AutoComplete
            v-if="radioGroup === 'perName'"
            kind="employee"
            :data="employeeIds"
            :companyId="company.id"
            :placeholder="$t('Components.ApplyToAllDialog.searchEmployees')"
            :isValid="radioGroup === 'perName' && isEmployeeValid"
            :required="radioGroup === 'perName'"
            multiple
            @input="updateEmployee"
          />

          <FormTextField
            v-if="radioGroup === 'perId'"
            v-model="employeesIdsField"
            :name="$t(`${namespace}.fieldIdsName`)"
            :placeholder="$t(`${namespace}.fieldIdsPlaceholder`)"
            :disabled="isLoading"
            :v-validate="{ required: true }"
            ref="name"
            @blur="normalizeIDs"
          />

          <p><strong>{{ $t(`${namespace}.setWebCallPermissionStep2`) }}</strong></p>
          <v-radio-group
            v-model="radioGroupPermission"
            v-validate="{ required: true }"
            :error-messages="errors.collect($t(`${namespace}.fieldPermissionName`))"
            :data-vv-name="$t(`${namespace}.fieldPermissionName`)"
            required
            @change="getWebCallPermission"
          >
            <v-radio
              v-for="(item, index) in $t(`${namespace}.setWebCallPermissionItems`)"
              :key="index"
              :label="item.label"
              :value="item.value"
            />
          </v-radio-group>
        </v-card-text>

        <v-divider />

        <v-card-actions class="dialog__footer">
          <v-spacer />

          <a
            v-if="!isLoading"
            class="mr-4"
            @click.prevent="close()"
          >
            {{ $t(`${namespace}.cancel`) }}
          </a>

          <Button
            type="submit"
            :loading="isLoading"
            :disabled="isLoading"
          >
            {{ $t(`${namespace}.updatePermissions`) }}
          </Button>
        </v-card-actions>
      </form>
    </div>
  </Dialog>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { isValidArray, catchError } from 'corp-front-vue-commons/src/utils/functions'
import AutoComplete from 'components/AutoComplete'

export default {
  name: 'SetWebCallPermissionBulk',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  components: {
    AutoComplete
  },
  computed: {
    ...mapState({
      company: ({ company }) => company.selectedCompany
    })
  },
  data () {
    return {
      namespace: 'Components.ApplyToAllDialog',
      radioGroup: null,
      isLoading: false,
      radioGroupPermission: null,

      // custom validation
      isEmployeeValid: true,

      // fields
      employeeIds: [],
      employeesIdsField: '',
      thirdPartyWebCallPermission: null,
      webCallPermission: null
    }
  },
  methods: {
    ...mapActions({
      showAlert: 'snackbar/show'
    }),

    isValidArray (val) {
      return isValidArray(val)
    },

    setLoadingValue (boolean) {
      this.isLoading = boolean
    },

    resetEmployeeList () {
      this.employeeIds = []
      this.employeesIdsField = ''
    },

    resetAllFields () {
      this.resetEmployeeList()
      this.isEmployeeValid = true
      this.radioGroup = null
      this.radioGroupPermission = null
      this.thirdPartyWebCallPermission = null
      this.webCallPermission = null
    },

    forceFieldValidation (ref, selector) {
      const $field = this.$refs[ref]?.$el

      if ($field) {
        $field.querySelector(selector).focus()
        $field.querySelector(selector).blur()
      }
    },

    normalizeIDs () {
      if (this.employeesIdsField.trim().length) {
        this.employeesIdsField = this.employeesIdsField
          .replace(/[^\d,]/g, '')
          .replace(/,+/g, ',')

        this.employeeIds = this.employeesIdsField.split(',').map(Number).filter(Boolean)
      }
    },

    close (reload = false) {
      this.$emit('close', reload)
    },

    updateEmployee (val) {
      if (!this.isValidArray(val)) {
        this.employeeIds = []
        this.radioGroup !== 'allEmployees' && (this.isEmployeeValid = false)
        return
      }

      this.employeeIds = val
      this.radioGroup !== 'allEmployees' && (this.isEmployeeValid = true)
    },

    getWebCallPermission (val) {
      this.thirdPartyWebCallPermission = false
      this.webCallPermission = false

      if (val === 'onlyForEmployee') {
        this.webCallPermission = true
        return
      }

      if (val === 'onlyForThirdParties') {
        this.thirdPartyWebCallPermission = true
        return
      }

      this.thirdPartyWebCallPermission = true
      this.webCallPermission = true
    },

    async batchUpdate () {
      this.forceFieldValidation('name', 'input')

      if (this.radioGroup !== 'allEmployees' && !this.employeeIds.length) {
        this.isEmployeeValid = false
      }

      try {
        const isValid = await this.$validator.validateAll()

        if (this.radioGroup !== 'allEmployees' && !this.employeeIds.length) return

        if (isValid) {
          this.setLoadingValue(true)

          let ids = null

          if (this.radioGroup === 'perName') {
            const employeeIds = this.employeeIds.map(item => item.id)
            ids = this.employeeIds.length ? employeeIds : null
          } else {
            ids = this.employeeIds
          }

          await this.$apiService.employee.batchUpdate(
            this.company.id,
            { budget: null },
            { budgetDate: null },
            { budgetPeriod: 'monthly' },
            { credit: null },
            { useBudget: null },
            { useRideCredit: null },
            { categoryIds: null },
            { supervisorId: null },
            { thirdPartyWebCallPermission: this.thirdPartyWebCallPermission },
            { webCallPermission: this.webCallPermission },
            { ids },
            { v2Permission: true }
          )

          this.showAlert({
            type: 'success',
            message: this.$t(`${this.namespace}.setWebCallPermissionSuccess`)
          })

          this.resetAllFields()
          this.close(true)
          this.setLoadingValue(false)
        }
      } catch (error) {
        this.showAlert({
          type: 'error',
          message: this.$t(`${this.namespace}.error`),
          timeout: 6000
        })

        this.setLoadingValue(false)
        catchError(error)
      }
    }
  },
  watch: {
    radioGroup () {
      this.resetEmployeeList()
    },

    employeesIdsField (val) {
      if (!val) this.resetEmployeeList()
    },

    visible (val) {
      // fix validation error when close dialog
      this.$validator._paused = !val

      this.resetAllFields()
      this.setLoadingValue(false)

      val && this.$ga.page('/dialog/colaboradores/permissoes-para-chamada-web-em-massa')
    }
  }
}
</script>
